import type { ReactNode } from 'react';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { AuthLayout } from './AuthLayout.tsx';
import type { AuthAside } from './AuthPanel.tsx';
import { Button } from './components/button.tsx';
import { Field, FieldLabel } from './components/field.tsx';
import { Input } from './components/input.tsx';
import { PasswordInput } from './PasswordInput.tsx';

export function LoginPage({
  heading,
  signIn,
  redirectTo = '/',
  intro,
  aside,
  siteUrl,
  forgotPasswordTo,
  footer,
  altLink,
}: {
  heading: string;
  signIn: (email: string, password: string) => Promise<{ error?: { message?: string } | null }>;
  redirectTo?: string;
  intro?: ReactNode;
  aside?: AuthAside;
  siteUrl?: string | undefined;
  /** Route for a "Forgot password?" link under the Sign in button. */
  forgotPasswordTo?: string;
  footer?: ReactNode;
  altLink?: ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation() as { state?: { reason?: string; notice?: string } };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(location.state?.reason ?? null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await signIn(email, password);
    setBusy(false);
    if (err) {
      setError(err.message ?? 'Sign-in failed');
      return;
    }
    void navigate(redirectTo, { replace: true });
  }

  return (
    <AuthLayout
      title={heading}
      intro={intro}
      aside={aside}
      siteUrl={siteUrl}
      footer={footer}
      altLink={altLink}
    >
      <form className="grid gap-4" onSubmit={submit}>
        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input
            size="lg"
            id="email"
            type="email"
            autoComplete="username"
            placeholder="you@example.com"
            icon={<Mail />}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="password">Password</FieldLabel>
          <PasswordInput
            size="lg"
            id="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {location.state?.notice && !error && (
          <p role="status" className="m-0 text-sm text-success">
            {location.state.notice}
          </p>
        )}
        {error && (
          <p role="alert" className="m-0 text-sm text-destructive">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        {/* After the button so Tab goes email → password → Sign in. */}
        {forgotPasswordTo && (
          <Link
            to={forgotPasswordTo}
            className="justify-self-center text-sm text-primary underline-offset-4 hover:underline"
          >
            Forgot password?
          </Link>
        )}
      </form>
    </AuthLayout>
  );
}
